import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Reusable hook for searchable select/autocomplete functionality
 * 
 * @param {Function} dispatchAction - Redux action to dispatch for search (e.g., GetVendors)
 * @param {Function} selector - Redux state selector to get search results (e.g., state => state.pay.vendors)
 * @param {string} companyId - Company ID required for API calls
 * @param {Object} options - Configuration options
 * @param {number} options.page - Pagination page (default: 1)
 * @param {number} options.limit - Results limit per page (default: 10)
 * @param {number} options.debounceDelay - Debounce delay in ms (default: 300)
 * @param {Function} options.formatQuery - Custom query formatter (default: name=${query})
 * 
 * @returns {Object} - { results, loading, query, searchQuery, handleSearch, reset }
 * 
 * @example
 * // For vendors search
 * const { results: vendors, loading, handleSearch } = useSearchableSelect(
 *   GetVendors,
 *   state => state.pay.vendors,
 *   companyId,
 *   { limit: 15, formatQuery: (q) => `name=${q}` }
 * );
 * 
 * @example
 * // For beneficiaries search
 * const { results: beneficiaries, loading, handleSearch } = useSearchableSelect(
 *   GetVendors,
 *   state => state.pay.vendors,
 *   companyId,
 *   { formatQuery: (q) => `supplier_name=${q}` }
 * );
 */
export const useSearchableSelect = (
  dispatchAction,
  selector,
  companyId,
  options = {}
) => {
  const {
    page = 1,
    limit = 10,
    debounceDelay = 300,
    formatQuery = (q) => `name=${q}`,
  } = options;

  const dispatch = useDispatch();
  const results = useSelector(selector);

  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const debounceTimerRef = useRef(null);
  const cancellableRef = useRef(null);

  /**
   * Executes the search with debouncing
   */
  const handleSearch = useCallback(
    (searchQuery) => {
      setQuery(searchQuery);

      // Clear existing debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        if (searchQuery.trim() === '') {
          setLoading(false);
          return;
        }

        // Cancel previous request if exists
        if (cancellableRef.current) {
          cancellableRef.current.cancel = true;
        }

        // Create new cancellable object
        const cancellableObj = { cancel: false };
        cancellableRef.current = cancellableObj;

        // Dispatch the action with formatted query
        const formattedQuery = formatQuery(searchQuery);
        dispatch(
          dispatchAction(companyId, page, limit, formattedQuery, cancellableObj, setLoading)
        );
      }, debounceDelay);
    },
    [dispatch, dispatchAction, companyId, page, limit, debounceDelay, formatQuery]
  );

  /**
   * Reset search state
   */
  const reset = useCallback(() => {
    setQuery('');
    setLoading(false);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (cancellableRef.current) {
      cancellableRef.current.cancel = true;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (cancellableRef.current) {
        cancellableRef.current.cancel = true;
      }
    };
  }, []);

  return {
    results,
    loading,
    query,
    handleSearch,
    reset,
  };
};

export default useSearchableSelect;
